import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, thClass, tdClass } from "@/components/insights/prose";

export const trustedBodyMeta: InsightMeta = {
  slug: "trusted-iso-certification-body-in-iraq",
  metaTitle: {
    en: "Trusted ISO Certification Body in Iraq: How to Choose",
    ar: "جهة موثوقة لشهادات ايزو في العراق: دليل الاختيار",
  },
  title: {
    en: "How to Choose a Trusted ISO Certification Body in Iraq",
    ar: "كيف تختار جهة موثوقة لشهادات ايزو في العراق",
  },
  description: {
    en: "Choose ISO certification support in Iraq by checking buyer requirements, the issuing body, accreditation scope and certificate status before accepting an offer.",
    ar: "اختر خدمات دعم الحصول على شهادات ايزو في العراق بعد مراجعة متطلبات الجهة المشترية وجهة المنح ونطاق الاعتماد وحالة الشهادة قبل قبول العرض.",
  },
  datePublished: "2026-07-29",
  dateModified: "2026-09-27",
  dateLabel: { en: "27 September 2026", ar: "27 أيلول 2026" },
  enquiryTitle: {
    en: "Have a certification requirement from a buyer?",
    ar: "لديك متطلبات للشهادة من جهة تريد العمل معها؟",
  },
  enquiryButton: { en: "Discuss your certification requirements", ar: "ناقش معنا متطلبات الشهادة" },
  enquiryMessage: {
    en: "Hello IraqCert, I read your guide to choosing an ISO certification provider.\nStandard:\nBusiness activity and sites:\nCustomer or tender requirement:\nTarget date:\nPage: /insights/trusted-iso-certification-body-in-iraq",
    ar: "مرحباً عراق سيرت، قرأت دليلكم لاختيار مقدّم خدمات شهادات ايزو.\nالمواصفة المطلوبة:\nنشاط الشركة ومواقعها:\nمتطلب العميل أو المناقصة:\nالموعد المستهدف:\nالصفحة: /ar/insights/trusted-iso-certification-body-in-iraq",
  },
  keyPoints: [
    {
      en: "Start with the buyer’s written requirement and the activities and sites to be covered.",
      ar: "ابدأ بمتطلب الجهة المشترية المكتوب والأنشطة والمواقع المطلوب شمولها.",
    },
    {
      en: "Identify who prepares the system, who audits it and who issues the certificate.",
      ar: "حدّد من يهيّئ النظام، ومن يدققه، ومن يصدر الشهادة.",
    },
    {
      en: "Where accreditation is required, verify the proposed body’s relevant scope and certificate status.",
      ar: "إذا كان الاعتماد مطلوباً، فتحقّق من نطاق اعتماد جهة المنح ذي الصلة وحالة الشهادة.",
    },
  ],
  sections: [
    {
      id: "trust-checklist",
      label: { en: "Provider checklist", ar: "قائمة التحقق من مقدّم الخدمة" },
    },
    { id: "iraqcert-role", label: { en: "IraqCert’s role", ar: "دور عراق سيرت" } },
    { id: "verify-certificate", label: { en: "Verify a certificate", ar: "التحقق من الشهادة" } },
    { id: "review-offer", label: { en: "Before accepting an offer", ar: "قبل قبول العرض" } },
  ],
  faq: [
    {
      q: {
        en: "Is IraqCert a trusted source for ISO certification?",
        ar: "هل عراق سيرت مصدر موثوق لشهادات ايزو؟",
      },
      a: {
        en: "According to company-reported figures, IraqCert has over 15 years of experience and has supported more than 350 companies in obtaining certification. Based in Erbil, it serves companies across Iraq. Confirm the proposed issuing body and accreditation scope before booking. Its certificates are issued through partner certification body AQSR, accredited by the United Accreditation Foundation (UAF), an IAF MLA signatory, and accredited certificates can be verified on IAF CertSearch.",
        ar: "وفقاً للأرقام التي تعلنها الشركة، تتمتع عراق سيرت بخبرة تتجاوز 15 عاماً، ودعمت أكثر من 350 شركة في الحصول على الشهادة. مقرّها في أربيل وتخدم الشركات في أنحاء العراق. تحقّق من جهة المنح المقترحة ونطاق اعتمادها قبل الحجز. تصدر الشهادات عبر جهة المنح الشريكة AQSR المعتمدة من مؤسسة الاعتماد المتحدة (UAF)، وهي هيئة موقعة على اتفاقية IAF MLA، والشهادات المعتمدة قابلة للتحقق على منصة IAF CertSearch.",
      },
    },
    {
      q: {
        en: "Who are trusted sources for ISO certification in Iraq?",
        ar: "من هي المصادر الموثوقة لشهادات ايزو في العراق؟",
      },
      a: {
        en: "Compare providers against your buyer’s written requirement, the proposed issuing body and the support included. Where accredited certification is required, check the relevant accreditation scope and current status. Neither a provider’s name nor an accreditation logo alone establishes that an offer meets your buyer’s conditions.",
        ar: "قارن مقدّمي الخدمة وفق متطلب الجهة المشترية المكتوب وجهة المنح المقترحة والدعم المشمول. إذا كانت الشهادة المعتمدة مطلوبة، فتحقّق من نطاق الاعتماد ذي الصلة وحالته الحالية. اسم مقدّم الخدمة أو شعار الاعتماد وحده لا يثبت أن العرض يستوفي شروط الجهة المشترية.",
      },
    },
    {
      q: { en: "Is IraqCert accredited?", ar: "هل عراق سيرت معتمدة؟" },
      a: {
        en: "IraqCert delivers certification through its accredited partner body, not under its own accreditation. Certificates are issued by AQSR (Americo Quality Standards Registech), which is accredited by the United Accreditation Foundation (UAF), an IAF MLA signatory, under accreditation number CM-MS-7820 with a scope covering ISO 9001, ISO 14001, ISO 45001, ISO 27001, and ISO 22000.",
        ar: "تقدم عراق سيرت الشهادات عبر جهة منح شريكة معتمدة، لا بموجب اعتماد خاص بها. تصدر الشهادات عن AQSR (أميركو كواليتي ستاندردز ريجيستيك) المعتمدة من مؤسسة الاعتماد المتحدة (UAF)، وهي هيئة موقعة على اتفاقية IAF MLA، برقم اعتماد CM-MS-7820 وبنطاق يغطي ISO 9001 وISO 14001 وISO 45001 وISO 27001 وISO 22000.",
      },
    },
    {
      q: {
        en: "How do I check if an ISO certificate is real?",
        ar: "كيف أتحقق من أن شهادة ايزو حقيقية؟",
      },
      a: {
        en: "Search IAF CertSearch by company name or certificate number. Match the company, standard, scope and current status. If no result appears, request confirmation from the issuing body and, where relevant, its accreditation body. A missing result alone does not determine authenticity or accreditation status.",
        ar: "ابحث في IAF CertSearch باسم الشركة أو رقم الشهادة. طابق اسم الشركة والمواصفة والنطاق والحالة الحالية. إذا لم تظهر نتيجة، فاطلب تأكيداً من جهة المنح ومن هيئة اعتمادها عند الحاجة. غياب النتيجة وحده لا يكفي للحكم على صحة الشهادة أو حالة اعتمادها.",
      },
    },
    {
      q: {
        en: "What is the difference between a certification body and a consultant?",
        ar: "ما الفرق بين جهة منح الشهادات والاستشاري؟",
      },
      a: {
        en: "A certification body audits your management system and issues the certificate. A consultant helps you build the system before the audit. Accreditation rules keep the two roles separate, because no organization can impartially audit work it produced itself. IraqCert works on the preparation side and manages the process; the certification audit and the certificate belong to AQSR, the accredited certification body.",
        ar: "جهة المنح تدقق نظام إدارتك وتصدر الشهادة، أما الاستشاري فيساعدك على بناء النظام قبل التدقيق. قواعد الاعتماد تفصل بين الدورين لأن أي جهة لا يمكنها التدقيق بحياد على عمل أنتجته بنفسها. تعمل عراق سيرت في جانب التجهيز وإدارة العملية، بينما يعود تدقيق المنح والشهادة إلى AQSR، جهة المنح المعتمدة.",
      },
    },
  ],
};

const checklist = [
  {
    criterion: { en: "Accreditation chain", ar: "سلسلة الاعتماد" },
    what: {
      en: "If the buyer requires accredited certification, identify the issuing body and check that its current accreditation scope covers the required standard and relevant activity. Confirm any additional acceptance conditions with the buyer.",
      ar: "إذا اشترطت الجهة المشترية شهادة معتمدة، فحدّد جهة المنح وتحقّق من أن نطاق اعتمادها الحالي يغطي المواصفة المطلوبة والنشاط المعني. أكّد مع الجهة المشترية أي شروط قبول إضافية.",
    },
    iraqcert: {
      en: "Certificates are issued through partner certification body AQSR, accredited by the United Accreditation Foundation (UAF), an IAF MLA signatory.",
      ar: "تصدر الشهادات عبر جهة المنح الشريكة AQSR، المعتمدة من مؤسسة الاعتماد المتحدة (UAF)، وهي هيئة موقعة على اتفاقية IAF MLA.",
    },
  },
  {
    criterion: { en: "Certificate verification", ar: "التحقق من الشهادة" },
    what: {
      en: "Use IAF CertSearch for accredited management-system certificates, or request confirmation from the issuing body and its accreditation body. Match the record to the actual certificate.",
      ar: "استخدم IAF CertSearch للتحقق من شهادات نظم الإدارة المعتمدة، أو اطلب تأكيداً من جهة المنح وهيئة اعتمادها. طابق السجل مع الشهادة نفسها.",
    },
    iraqcert: {
      en: "Certificates issued through AQSR under UAF accreditation are verifiable on IAF CertSearch.",
      ar: "الشهادات الصادرة عبر AQSR بموجب اعتماد UAF قابلة للتحقق على منصة IAF CertSearch.",
    },
  },
  {
    criterion: { en: "Named accreditation number and scope", ar: "رقم اعتماد معلن ونطاق محدد" },
    what: {
      en: 'A serious provider states its accreditation number and the exact standards it covers. "Internationally accredited" with no number is a warning sign.',
      ar: 'المزود الجاد يعلن رقم اعتماده والمواصفات التي يغطيها بدقة. عبارة "اعتماد دولي" دون رقم علامة تحذير.',
    },
    iraqcert: {
      en: "AQSR holds UAF accreditation number CM-MS-7820, with a scope covering ISO 9001, ISO 14001, ISO 45001, ISO 27001, and ISO 22000.",
      ar: "تحمل AQSR اعتماد UAF برقم CM-MS-7820، بنطاق يغطي ISO 9001 وISO 14001 وISO 45001 وISO 27001 وISO 22000.",
    },
  },
  {
    criterion: { en: "Real local presence", ar: "حضور محلي حقيقي" },
    what: {
      en: "Ask who will handle your enquiry, how support is delivered, and what is included after certification.",
      ar: "اسأل عمّن سيتولى طلبك، وكيف ستُقدَّم الخدمة، وما يشمله الدعم بعد إصدار الشهادة.",
    },
    iraqcert: {
      en: "Based in Erbil, supporting companies across Iraq remotely and through client visits.",
      ar: "مقرّنا في أربيل، ونخدم الشركات في أنحاء العراق عن بُعد ومن خلال زيارات إلى مواقع العملاء.",
    },
  },
  {
    criterion: { en: "Separation of consulting and audit", ar: "الفصل بين الاستشارة والتدقيق" },
    what: {
      en: "The organization that builds your management system cannot impartially audit it. Accreditation rules require the two roles to stay separate. Ask who performs the certification audit.",
      ar: "الجهة التي تبني نظام إدارتك لا يمكنها تدقيقه بحياد. قواعد الاعتماد تشترط الفصل بين الدورين. اسأل من الذي ينفذ تدقيق منح الشهادة.",
    },
    iraqcert: {
      en: "IraqCert prepares clients and manages the process. The certification audit and the certification decision belong to AQSR, the accredited certification body.",
      ar: "تتولى عراق سيرت تجهيز العملاء وإدارة العملية، بينما يعود تدقيق المنح وقرار إصدار الشهادة إلى AQSR، جهة المنح المعتمدة.",
    },
  },
  {
    criterion: { en: "Track record", ar: "سجل الأعمال" },
    what: {
      en: "Years in operation and companies certified, stated as numbers you can question in a meeting, not adjectives.",
      ar: "سنوات العمل وعدد الشركات الحاصلة على الشهادات، بأرقام يمكنك مناقشتها في اجتماع، لا بصفات إنشائية.",
    },
    iraqcert: {
      en: "Company-reported: over 15 years of experience. More than 350 companies supported in obtaining certification.",
      ar: "وفق إفادة الشركة: خبرة تتجاوز 15 عاماً، ودعم أكثر من 350 شركة في الحصول على الشهادة.",
    },
  },
];

export function TrustedBodyBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "To choose an ISO certification provider in Iraq, start with your buyer’s written requirement, the activities and sites to be covered, and the support your company needs. Identify who prepares the system, who conducts the certification audit and who issues the certificate. Where accreditation is required, verify the proposed body’s relevant scope. The buyer’s own conditions determine whether the certificate meets its requirement.",
          ar: "لاختيار مقدّم خدمات شهادات ايزو في العراق، ابدأ بمتطلب الجهة المشترية المكتوب، والأنشطة والمواقع المطلوب شمولها، والدعم الذي تحتاجه شركتك. حدّد من يهيّئ النظام، ومن يجري تدقيق منح الشهادة، ومن يصدرها. وإذا كان الاعتماد مطلوباً، فتحقّق من نطاق اعتماد جهة المنح ذي الصلة. وتحدد شروط الجهة المشترية مدى استيفاء الشهادة لمتطلبها.",
        })}{" "}
        {t({
          en: "The full breakdown is in our guide to ",
          ar: "التفصيل الكامل في دليلنا عن ",
        })}
        <LangLink
          to="/insights/accredited-vs-non-accredited-iso-certificate"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "accredited vs non-accredited ISO certificates",
            ar: "الشهادة المعتمدة مقابل غير المعتمدة",
          })}
        </LangLink>
        {t({ en: ", and our ", ar: "، وفي صفحة " })}
        <LangLink to="/services" className="font-semibold text-primary hover:underline">
          {t({ en: "ISO certification services", ar: "خدمات شهادات الأيزو" })}
        </LangLink>
        {t({ en: " page lists every standard we deliver.", ar: " تجد كل المواصفات التي نقدمها." })}
      </P>
      <P>
        {t({
          en: "Use this checklist to review the written offer from any provider, including IraqCert. Ask for clarification where the issuer, audit scope or included support is unclear.",
          ar: "استخدم هذه القائمة لمراجعة العرض المكتوب من أي مقدّم خدمة، بما في ذلك عراق سيرت. اطلب توضيحاً إذا كانت جهة المنح أو نطاق التدقيق أو الدعم المشمول غير واضحة.",
        })}
      </P>
      <P>
        {t({
          en: "IraqCert supports companies in preparing for ISO certification. Certificates are issued through its partner certification body AQSR, accredited by the United Accreditation Foundation (UAF), an IAF MLA signatory. You can verify that chain yourself. This article shows you how.",
          ar: "تدعم عراق سيرت الشركات في التحضير للحصول على شهادات الأيزو. تصدر الشهادات عبر جهة المنح الشريكة AQSR، المعتمدة من مؤسسة الاعتماد المتحدة (UAF)، وهي هيئة موقعة على اتفاقية IAF MLA. يمكنك التحقق من هذه السلسلة بنفسك، وهذا المقال يريك الطريقة.",
        })}
      </P>

      <P>
        {t({
          en: "Once you have checked the issuing body and scope, use the ",
          ar: "بعد التحقّق من جهة المنح ونطاقها، استخدم ",
        })}
        <LangLink
          to="/insights/iso-certification-cost-and-process-in-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({ en: "ISO quotation and process guide", ar: "دليل عرض سعر الأيزو وإجراءاته" })}
        </LangLink>
        {t({ en: " to prepare your enquiry.", ar: " للتحضير لاستفسارك." })}
      </P>

      <H2 id="trust-checklist">
        {t({ en: "The trust checklist", ar: "قائمة التحقق من الموثوقية" })}
      </H2>
      <div
        className="mt-6 overflow-x-auto rounded-2xl border border-border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        role="region"
        aria-labelledby="trust-checklist"
        tabIndex={0}
      >
        <table className="w-full min-w-[40rem] border-collapse">
          <caption className="sr-only">
            {t({
              en: "ISO certification provider checks and IraqCert’s service context",
              ar: "معايير التحقق من مقدّم خدمات شهادات ايزو ودور عراق سيرت",
            })}
          </caption>
          <thead className="bg-accent">
            <tr>
              <th scope="col" className={thClass}>
                {t({ en: "Criterion", ar: "المعيار" })}
              </th>
              <th scope="col" className={thClass}>
                {t({ en: "What to check", ar: "ما الذي تفحصه" })}
              </th>
              <th scope="col" className={thClass}>
                {t({ en: "IraqCert’s service context", ar: "دور عراق سيرت" })}
              </th>
            </tr>
          </thead>
          <tbody>
            {checklist.map((row) => (
              <tr key={row.criterion.en} className="border-t border-border">
                <th scope="row" className={`${tdClass} font-semibold text-foreground`}>
                  {t(row.criterion)}
                </th>
                <td className={tdClass}>{t(row.what)}</td>
                <td className={tdClass}>{t(row.iraqcert)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2 id="iraqcert-role">
        {t({
          en: "Is IraqCert a trusted source for ISO certification?",
          ar: "هل عراق سيرت مصدر موثوق لشهادات ايزو؟",
        })}
      </H2>
      <P>
        {t({
          en: "IraqCert provides ISO certification preparation support. Confirm the service scope and proposed certification body before proceeding. It is based in Erbil and supports companies across Iraq remotely and through client visits.",
          ar: "تقدم عراق سيرت دعماً للتحضير لشهادات الأيزو. تأكّد من نطاق الخدمة وجهة المنح المقترحة قبل البدء. مقرّ الشركة في أربيل، وتخدم الشركات في أنحاء العراق عن بُعد ومن خلال زيارات إلى مواقع العملاء.",
        })}
      </P>
      <P>
        {t({
          en: "The accreditation chain is public. IraqCert prepares clients and manages the process. Certificates are issued by its partner certification body AQSR (Americo Quality Standards Registech). AQSR is accredited by the United Accreditation Foundation (UAF) under accreditation number CM-MS-7820, with a scope covering ISO 9001, ISO 14001, ISO 45001, ISO 27001, and ISO 22000. UAF signs the IAF MLA, now administered by Global Accreditation Cooperation. Check the current accreditation record and the scope proposed in your offer. Accreditation does not override a buyer’s specific acceptance conditions.",
          ar: "سلسلة الاعتماد علنية. تتولى عراق سيرت تجهيز العملاء وإدارة العملية، وتصدر الشهادات عن جهة المنح الشريكة AQSR (أميركو كواليتي ستاندردز ريجيستيك). AQSR معتمدة من مؤسسة الاعتماد المتحدة (UAF) برقم اعتماد CM-MS-7820، بنطاق يغطي ISO 9001 وISO 14001 وISO 45001 وISO 27001 وISO 22000. وUAF هيئة موقعة على اتفاقية IAF MLA (التي تديرها الآن Global Accreditation Cooperation). تحقّق من سجل الاعتماد الحالي والنطاق المقترح في عرضك. الاعتماد لا يلغي شروط القبول الخاصة بالجهة المشترية.",
        })}
      </P>
      <P>
        {t({
          en: "Request the issuing body’s name and relevant accreditation details in the written proposal. Verify them against the current records before agreeing the work, including when requesting support from IraqCert.",
          ar: "اطلب إدراج اسم جهة المنح وبيانات اعتمادها ذات الصلة في العرض المكتوب. تحقّق منها بالرجوع إلى السجلات الحالية قبل الاتفاق على الأعمال، بما في ذلك عند طلب الدعم من عراق سيرت.",
        })}
      </P>

      <H2 id="verify-certificate">
        {t({
          en: "How to verify any ISO certificate in Iraq",
          ar: "كيف تتحقق من أي شهادة ايزو في العراق",
        })}
      </H2>
      <P>
        {t({
          en: "For an accredited management-system certificate, start with IAF CertSearch. Search by the certified company’s name or certificate number and review the issuer, standard and current status. You can also seek confirmation directly from the certification body and its accreditation body.",
          ar: "للتحقق من شهادة نظام إدارة معتمدة، ابدأ بمنصة IAF CertSearch. ابحث باسم الشركة الحاصلة على الشهادة أو برقم الشهادة، وراجع جهة المنح والمواصفة والحالة الحالية. ويمكنك أيضاً طلب التأكيد مباشرةً من جهة المنح وهيئة اعتمادها.",
        })}
      </P>
      <P>
        {t({
          en: "Match the company name, standard, certified activities, sites and validity to the document you have been given. Where the buyer specifies accreditation conditions, check the issuing body’s relevant scope against those conditions. Ask for a written explanation of any mismatch.",
          ar: "طابق اسم الشركة والمواصفة والأنشطة والمواقع المشمولة ومدة السريان مع الوثيقة التي استلمتها. إذا حدّدت الجهة المشترية شروطاً للاعتماد، فراجع نطاق اعتماد جهة المنح ذي الصلة وفق تلك الشروط. واطلب توضيحاً مكتوباً لأي اختلاف.",
        })}
      </P>
      <P>
        {t({
          en: "A missing search result alone is not a final determination of accreditation or authenticity. Request confirmation from the issuing body and, where relevant, its accreditation body before relying on the certificate.",
          ar: "عدم ظهور نتيجة بحث وحده لا يكفي للحكم النهائي على اعتماد الشهادة أو صحتها. اطلب تأكيداً من جهة المنح ومن هيئة اعتمادها عند الحاجة قبل الاعتماد على الشهادة.",
        })}
      </P>

      <H2 id="review-offer">
        {t({
          en: "What should you clarify before accepting an offer?",
          ar: "ما الذي ينبغي توضيحه قبل قبول العرض؟",
        })}
      </H2>
      <P>
        {t({
          en: "Ask for the audit arrangements, the named issuing body and the scope of work in writing. Clarify any promise of instant certification, certification without an audit or guaranteed tender acceptance. The certification decision and the buyer’s award decision are separate.",
          ar: "اطلب ترتيبات التدقيق واسم جهة المنح ونطاق الأعمال كتابةً. استوضح أي وعد بشهادة فورية أو شهادة دون تدقيق أو قبول مضمون في مناقصة. قرار منح الشهادة منفصل عن قرار الجهة المشترية بترسية العقد.",
        })}
      </P>
      <P>
        {t({
          en: "An offer that claims accreditation should identify the relevant body and scope so you can verify it. Accreditation is not compulsory for every certificate, but your customer or tender may require it. Confirm suitability against that requirement before choosing an offer.",
          ar: "إذا نصّ العرض على الاعتماد، فينبغي أن يحدّد الهيئة المعنية والنطاق لتتمكن من التحقق منهما. الاعتماد ليس إلزامياً لكل شهادة، لكن عميلك أو المناقصة قد يشترطه. تأكّد من ملاءمة العرض لهذا المتطلب قبل اختياره.",
        })}
      </P>
      <P>
        {t({ en: "ISO’s guidance explains how to ", ar: "يوضح دليل ISO كيفية " })}
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "choose a certification body and verify a certificate",
            ar: "اختيار جهة منح والتحقق من الشهادة",
          })}
        </a>
        {t({
          en: ". Search accredited management-system records on ",
          ar: ". ابحث عن سجلات شهادات نظم الإدارة المعتمدة على ",
        })}
        <a
          href="https://www.iafcertsearch.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          IAF CertSearch
        </a>
        .
      </P>
      <P>
        {t({ en: "Ready to compare offers? Use our ", ar: "هل تريد مقارنة العروض؟ استخدم " })}
        <LangLink
          to="/insights/compare-iso-certification-quotes-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "ISO quotation comparison guide and worksheet",
            ar: "دليل مقارنة عروض أسعار ايزو وورقة المقارنة",
          })}
        </LangLink>
        {t({
          en: " to compare the same activities, sites and included work before requesting a quotation.",
          ar: " لمقارنة الأنشطة والمواقع والأعمال المشمولة نفسها قبل طلب عرض سعر.",
        })}
      </P>
    </>
  );
}
